import React, { useMemo, useState } from "react";
import { Box, Button, Grid, Tooltip, IconButton, Card } from "@mui/material";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Tune as TuneIcon,
  FilterAltOff as ClearIcon,
} from "@mui/icons-material";
import {
  CustomAutocompleteSelect,
  CustomSelect,
} from "../../../components/select";
import { usePreOnboardingPRNList } from "../../../hooks/use-preboarding";
import { getTableConfig, TABLE_TYPES } from "../config/tableConfig";
import { FINANCIER_TYPES } from "../../preOnboarding/constants/documents.config";
import { useLeadIdList } from "../../../hooks/use-driver-lead";
import { ActionButton } from "../../../components/buttons";

const FilterToggleButton = ({ active, count, onClick }) => (
  <ActionButton
    onClick={onClick}
    icon={<TuneIcon fontSize="small" />}
    label="Filters"
    count={count}
    showCount
    active={count > 0}
  />
);

const OnboardingFilters = ({
  filters,
  onFilterChange,
  filterSearchValue,
  onSearchChange,
  onClearFilters,
  onRefresh,
  isFetching,
  isMobile,
  tableType,
}) => {
  const config = getTableConfig(tableType);
  const [showFilters, setShowFilters] = useState(false);

  const filterDefs = config.filters || [];

  const isPRNTable =
    tableType === TABLE_TYPES.PRE_ONBOARDING ||
    tableType === TABLE_TYPES.ONBOARDED;
  const isLeadIdTable = tableType === TABLE_TYPES.LEADS;

  const {
    data: preOnboardingList,
    isLoading: isLoadingPRNList,
    refetch: refetchPNRList,
  } = usePreOnboardingPRNList({ enabled: !!(isPRNTable && tableType) });

  const {
    data: leadIds,
    isLoading: isLoadingLeadIds,
    refetch: refetchLeadIdList,
  } = useLeadIdList({ enabled: !!(isLeadIdTable && tableType) });

  const prnOrLeadIdOptions = useMemo(() => {
    if (isPRNTable && preOnboardingList) {
      return [...new Set(preOnboardingList)].map((prn) => ({
        label: prn,
        value: prn,
      }));
    }
    if (isLeadIdTable && leadIds) {
      return [...new Set(leadIds)].map((id) => ({ label: id, value: id }));
    }
    return [];
  }, [preOnboardingList, leadIds, isPRNTable, isLeadIdTable]);

  const isSearchLoading = isPRNTable ? isLoadingPRNList : isLoadingLeadIds;

  const activeFilterCount = useMemo(() => {
    const fieldCount = filterDefs.filter((f) => filters?.[f.id]).length;
    return fieldCount + (filterSearchValue ? 1 : 0);
  }, [filterDefs, filters, filterSearchValue]);

  const handleRefresh = () => {
    onRefresh();
    if (isPRNTable) refetchPNRList();
    if (isLeadIdTable) refetchLeadIdList();
  };

  return (
    <Card
      sx={{
        p: isMobile ? 1.5 : 2,
        mb: 3,
        borderRadius: 3,
        border: "1px solid #E6ECEA",
        boxShadow: "0 1px 2px rgba(16,24,22,0.04)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: isMobile ? "100%" : 220,
            display: "flex",
            alignItems: "center",
            gap: 1,
            border: "1px solid #E6ECEA",
            borderRadius: 2,
            px: 1.25,
            "&:focus-within": {
              borderColor: "#0E7A6E",
            },
          }}
        >
          <SearchIcon sx={{ fontSize: 19, color: "#98A6A0" }} />
          <Box sx={{ flex: 1 }}>
            <CustomAutocompleteSelect
              placeholder={isPRNTable ? "Search by PRN" : "Search by lead id"}
              value={filterSearchValue ?? null}
              onChange={onSearchChange}
              options={prnOrLeadIdOptions}
              loading={isSearchLoading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  border: "none",
                  padding: "0 !important",
                  minHeight: 45,

                  "& fieldset": {
                    border: "none",
                  },

                  "&:hover fieldset": {
                    border: "none",
                  },

                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },

                "& .MuiInputBase-input": {
                  padding: "8px 0 !important",
                },
              }}
            />
          </Box>
        </Box>

        <FilterToggleButton
          active={showFilters || activeFilterCount > 0}
          count={activeFilterCount}
          onClick={() => setShowFilters((prev) => !prev)}
        />

        <ActionButton
          onClick={handleRefresh}
          disabled={isFetching}
          icon={<RefreshIcon fontSize="small" />}
          label="Refresh"
        />

        <ActionButton
          onClick={onClearFilters}
          icon={<ClearIcon fontSize="small" />}
          label="Clear"
        />
      </Box>

      {showFilters && filterDefs.length > 0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #E6ECEA" }}>
          <Grid container spacing={2}>
            {filterDefs.map((filter) => {
              if (!filter?.type) return null;
              return (
                <Grid item xs={12} sm={6} key={filter.id}>
                  <CustomSelect
                    label={filter.label}
                    value={filters?.[filter.id] ?? ""}
                    onChange={(e) => onFilterChange(filter.id, e.target.value)}
                    options={filter.options}
                    required={false}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Card>
  );
};

export default OnboardingFilters;
